import * as TableauUI from '@tableau/tableau-ui';
import { ArrayOfSideTabElements } from '@tableau/tableau-ui';
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { EventUtilsOptOut, PropsAndElementAttrs } from '../../utils/ReactHelpers';

export { ArrayOfSideTabElements };

export type SideTabsPaneProps = TableauUI.SideTabsPaneProps & {
  /** Will be added to the root element with the suffix `-SideTabsPaneWidget` */
  testId?: string;
};

/**
 * A composition widget which can be used to combine a `SideTabsWidget` with a content pane that the tabs control.
 * The correct ARIA attributes between the tab list and pane are applied automatically.
 *
 * There are two ways to provide the pane content:
 * * Controlled mode: If one child is provided, it will be rendered as the pane content. Update it in response to the `onChangeTab` prop on `tablist`.
 * * Uncontrolled mode: If an array of children is provided, they will be mapped into the list of tabs,
 *    i.e. `children[2]` will be rendered in the pane when tab index `2` is selected. In this case the widget will handle the selection state
 *    internally.
 *
 * Dimensions may be constrained via the usual routes, e.g. `style={{ height: 300, width: 600 }}`.
 * Both the side tabs and the content pane will scroll if they exceed the height of the overall widget.
 */
export const SideTabsPaneWidget: React.FC<PropsAndElementAttrs<SideTabsPaneProps>> = ({ testId, ...props }) => {
  return <TableauUI.SideTabsPane data-tb-test-id={makeTestId(testId, SideTabsPaneWidget.displayName)} {...props} />;
}
SideTabsPaneWidget.displayName = 'SideTabsPaneWidget';

export type SideTabsProps = TableauUI.SideTabsProps & {
  /** Will be added to the root element with the suffix `-SideTabsWidget` */
  testId?: string;
}

/**
 * A side (vertical) tabs widget. Generally expected to be used inside of a `SideTabsPaneWidget` but may be used separately if necessary.
 *
 * One of `aria-label` or `aria-labelledby` must be provided.
 * When used outside of a `SideTabsPaneWidget`, `aria-controls` must be added to each individual `SideTab`.
 */
// eslint-disable-next-line deprecation/deprecation
export const SideTabsWidget: React.FC<PropsAndElementAttrs<SideTabsProps>> = ({ testId, ...props }) =>
  <TableauUI.SideTabs data-tb-test-id={makeTestId(testId, SideTabsWidget.displayName)} {...props} />;
SideTabsWidget.displayName = 'SideTabsWidget';

export type SideTabProps = TableauUI.SideTabProps & {
  /** Will be added to the root element with the suffix `-SideTab` */
  testId?: string;
}

/** Accessibility requirements prohibit tooltips or other interactive content inside of a Tab. */
export const SideTab: React.FC<SideTabProps> = ({ testId, ...props }) =>
  <TableauUI.SideTab {...EventUtilsOptOut} data-tb-test-id={makeTestId(testId, SideTab.displayName)} {...props} />;
SideTab.displayName = 'SideTab';